import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { TenantSettingsUpdate } from '../api/useSettings.ts';

export type Utkast = TenantSettingsUpdate;

/**
 * Innstillingenes utkast, løftet OVER ruten (F32) — ETT per side (R5.3, P3).
 *
 * Siden sier det selv: «endringer lagres først ved «Lagre»». Fram til 2026-09-10 lå
 * utkastet i skjermkomponenten, så `g i` eller et klikk i sidemenyen kastet det uten
 * et ord — på den ene siden i panelet der arbeid kan gå tapt, og med en global
 * `g`-akkord som virker mens du står i feltet du nettopp fylte ut.
 *
 * Valget falt på å FJERNE tapet i stedet for å advare om det. En ruter-blokker var
 * alternativet, men `useBlocker` krever en data-ruter, og panelet bruker
 * `<BrowserRouter>`; å migrere roten for å kunne si «er du sikker?» er en stor endring
 * for å bevare et problem. Se «Utkastet i Innstillinger overlever navigasjon» i
 * tech-decisions.md.
 *
 * Prisen er at et utkast kan bli liggende ubemerket. Den betales av merket i
 * sidemenyen: står det endringer her, ser du det uansett hvilken side du er på.
 *
 * R5.3 delte flaten i to sider mot SAMME `PATCH /v1/admin/settings`. Utkastet er derfor
 * delt etter nøkkel og ikke slått sammen: hver side lagrer sitt eget, og en PATCH bærer
 * bare feltene fra siden man står på — utelatt felt betyr «ikke rør». Ett felles utkast
 * ville lagret Organisasjons endringer når man trykker «Lagre» på Innstillinger, uten at
 * foten der kunne si hva som ble sendt.
 *
 * H2.3 ga rommet Hypoteser sin egen Innstillinger-side mot samme PATCH, og med det sin egen
 * nøkkel: `hypoteser` bærer bare `hypothesis*`-feltene (vakt 9).
 */
export const UTKASTNOKLER = ['innstillinger', 'organisasjon', 'hypoteser'] as const;

export type Utkastnokkel = (typeof UTKASTNOKLER)[number];

export type Utkastantall = Record<Utkastnokkel, number>;

const TOMT: Utkastantall = { innstillinger: 0, organisasjon: 0, hypoteser: 0 };

interface UtkastStyring {
  utkast: Utkast;
  sett: <K extends keyof Utkast>(felt: K, verdi: Utkast[K]) => void;
  tøm: () => void;
  /** Tar feltet ut av utkastet — en verdi satt tilbake til den lagrede er ingen endring (H2.3). */
  fjern: (felt: keyof Utkast) => void;
  antall: number;
}

interface Styring {
  alle: Record<Utkastnokkel, Utkast>;
  sett: (nøkkel: Utkastnokkel, felt: keyof Utkast, verdi: Utkast[keyof Utkast]) => void;
  tøm: (nøkkel: Utkastnokkel) => void;
  fjern: (nøkkel: Utkastnokkel, felt: keyof Utkast) => void;
}

const Ctx = createContext<Styring | null>(null);

export function UtkastProvider({ children }: { children: ReactNode }) {
  const [alle, setAlle] = useState<Record<Utkastnokkel, Utkast>>({ innstillinger: {}, organisasjon: {}, hypoteser: {} });

  const sett = useCallback<Styring['sett']>((nøkkel, felt, verdi) => {
    setAlle((u) => ({ ...u, [nøkkel]: { ...u[nøkkel], [felt]: verdi } }));
  }, []);
  const tøm = useCallback<Styring['tøm']>((nøkkel) => setAlle((u) => ({ ...u, [nøkkel]: {} })), []);
  const fjern = useCallback<Styring['fjern']>((nøkkel, felt) => {
    setAlle((u) => {
      if (!(felt in u[nøkkel])) return u;
      const rest = { ...u[nøkkel] };
      delete rest[felt];
      return { ...u, [nøkkel]: rest };
    });
  }, []);

  const verdi = useMemo<Styring>(() => ({ alle, sett, tøm, fjern }), [alle, sett, tøm, fjern]);
  return <Ctx.Provider value={verdi}>{children}</Ctx.Provider>;
}

/**
 * Skallet leser antallene for merkene i sidemenyen og må tåle å stå UTENFOR provideren —
 * innloggingen og tofaktor rendrer skallet uten den. Derfor nuller og ikke et kast.
 */
export function useUtkastAntall(): Utkastantall {
  const v = useContext(Ctx);
  const alle = v?.alle;
  return useMemo(() => (alle ? { innstillinger: Object.keys(alle.innstillinger).length, organisasjon: Object.keys(alle.organisasjon).length, hypoteser: Object.keys(alle.hypoteser).length } : TOMT), [alle]);
}

export function useUtkast(nøkkel: Utkastnokkel): UtkastStyring {
  const v = useContext(Ctx);
  if (!v) throw new Error('[innspel] Innstillinger må stå inne i <UtkastProvider>.');
  const { alle, sett, tøm, fjern } = v;
  const utkast = alle[nøkkel];
  return useMemo<UtkastStyring>(
    () => ({
      utkast,
      sett: (felt, v2) => sett(nøkkel, felt, v2 as Utkast[keyof Utkast]),
      tøm: () => tøm(nøkkel),
      fjern: (felt) => fjern(nøkkel, felt),
      antall: Object.keys(utkast).length,
    }),
    [utkast, sett, tøm, fjern, nøkkel],
  );
}
